import {
	Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
	FormControl, InputLabel, ListItemText, MenuItem, Select, Stack, TextField
} from "@mui/material";
import { addEndPoint, updateEndPoint } from "api/layer";
import { AppAction, AppContext } from "helpers/context";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./add-modal.module.scss";

interface Props {
	open: boolean;
	onClose: () => void;
	onSave: () => void;
	data?: FormData;
}

interface FormData {
	id?: string;
	name: string;
	path: string;
	extraParam: string | null;
	httpMethod: string;
	serviceId: string | null;
	apiType: string;
	disabled: boolean;
	isPublic: boolean;
	description: string | null;
}

function EndPointAddModal(props: Props) {
	const [t] = useTranslation();
	const [context, dispatch] = useContext(AppContext);
	const [formData, setFormData] = useState<FormData>(
		props.data
			? { ...props.data }
			: {
				name: "",
				path: "",
				extraParam: null,
				httpMethod: "",
				serviceId: null,
				apiType: "",
				disabled: true,
				isPublic: false,
				description: null,
			}
	);
	const [isError, setIsError] = useState(false);

	const handleChange = (event: any) => {
		const { name, value, type, checked } = event.target;
		const val = type === "checkbox" ? checked : value;
		setFormData((prevData) => ({ ...prevData, [name]: val }));
	};

	const close = () => {
		props.onClose();
	};

	const handleSave = () => {
		let data = { ...formData };
		if (!data.name || !data.path || !data.httpMethod || !data.apiType) {
			setIsError(true);
			return;
		}
		setIsError(false);

		if (props.data) {
			updateEndPoint(data)
				.then((res: any) => {
					if (res && res.code === 200) {
						props.onClose();
						dispatch({
							type: AppAction.addSnackBarParams,
							payload: {
								title: "editEndPoint",
								message: "editEndPointSuccess",
							},
						});
					} else {
						dispatch({
							type: AppAction.addSnackBarParams,
							payload: {
								title: "editEndPoint",
								titleIconColor: "yellow",
								titleIcon: "warning",
								resMessage: res.message,
							},
						});
					}
				})
				.finally(() => {
					props.onSave();
				});
		} else {
			addEndPoint(data)
				.then((res: any) => {
					if (res && res.code === 200) {
						props.onClose();
						dispatch({
							type: AppAction.addSnackBarParams,
							payload: {
								title: "addEndPoint",
								message: "addEndPointSuccess",
							},
						});
					} else {
						dispatch({
							type: AppAction.addSnackBarParams,
							payload: {
								title: "addEndPoint",
								titleIconColor: "yellow",
								titleIcon: "warning",
								resMessage: res.message,
							},
						});
					}
				})
				.finally(() => {
					props.onSave();
				});
		}
	};

	return (
		<Dialog
			open={props.open}
			onClose={close}
		>
			<DialogTitle
				sx={{
					fontWeight: 600,
					fontSize: "16px",
					opacity: 0.79,
					color: "#333",
					width: "400px",
				}}
			>
				{props.data ? t("userSystem.editEndPoint") : t("userSystem.addEndPoint")}
			</DialogTitle>
			<DialogContent sx={{ paddingTop: "10px!important" }}>
				<Stack spacing={{ xy: 1, sm: 2 }}>
					<TextField
						required
						name="name"
						value={formData.name}
						error={isError && !formData.name}
						onChange={handleChange}
						label={t("userSystem.endPointName")}
						variant="outlined"
						color="success"
					/>
					<TextField
						required
						name="path"
						value={formData.path}
						error={isError && !formData.path}
						onChange={handleChange}
						label={t("userSystem.path")}
						variant="outlined"
						color="success"
					/>

					<FormControl
						fullWidth
						error={isError && !formData.httpMethod}
					>
						<InputLabel color="success" required>{t("userSystem.httpMethod")}</InputLabel>
						<Select
							value={formData.httpMethod}
							onChange={handleChange}
							name="httpMethod"
							label={t("userSystem.httpMethod")}
							renderValue={(selected) => selected}
							color="success"
						>
							<MenuItem
								value="GET"
							>
								<ListItemText primary="GET" />
							</MenuItem>
							<MenuItem
								value="POST"
							>
								<ListItemText primary="POST" />
							</MenuItem>
						</Select>
					</FormControl>

					<TextField
						name="extraParam"
						value={formData.extraParam}
						onChange={handleChange}
						label={t("userSystem.extraParam")}
						variant="outlined"
						color="success"
					/>

					<FormControl
						fullWidth
						error={isError && !formData.apiType}
					>
						<InputLabel color="success" required>{t("userSystem.endPointType")}</InputLabel>
						<Select
							value={formData.apiType}
							onChange={handleChange}
							name="apiType"
							label={t("userSystem.endPointType")}
							renderValue={(selected) => selected}
							color="success"
						>
							<MenuItem
								value="Event"
							>
								<ListItemText primary="Event" />
							</MenuItem>
							<MenuItem
								value="NatCat"
							>
								<ListItemText primary="NatCat" />
							</MenuItem>
							<MenuItem
								value="Crs"
							>
								<ListItemText primary="Crs" />
							</MenuItem>
						</Select>
					</FormControl>

					<TextField
						name="description"
						value={formData.description}
						onChange={handleChange}
						label={t("userSystem.description")}
						variant="outlined"
						color="success"
					/>
				</Stack>

				<FormControl>
					<div style={{ display: "flex", alignItems: "center" }}>
						<FormControlLabel
							label={t('userSystem.isPublic')}
							control={
								<Checkbox
									checked={formData.isPublic}
									onChange={handleChange}
									name="isPublic"
									color="success"
								/>
							}
						/>
						<FormControlLabel
							label={t('userSystem.editStatus')}
							control={
								<Checkbox
									checked={formData.disabled}
									onChange={handleChange}
									name="disabled"
									color="success"
								/>
							}
						/>
					</div>
				</FormControl>
			</DialogContent>
			<DialogActions>
				<Button
					onClick={close}
					color="success"
				>
					{t("buttons.cancel")}
				</Button>
				<Button
					onClick={handleSave}
					color="success"
					variant="contained"
				>
					{t("buttons.save")}
				</Button>
			</DialogActions>
		</Dialog>
	);
}

export default EndPointAddModal;
